import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  createActionsColumn,
  createSerialColumn,
  DataTable,
  DrillDownCount,
} from '@/components/common/DataTable';
import { EmptyState } from '@/components/common/EmptyState';
import { FilterField } from '@/components/common/FilterField';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { RowActionButton, RowActionGroup } from '@/components/common/RowActions';
import { WrappingCellText } from '@/components/common/DataTable';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { userDetailQueryOptions } from '@/features/users/api/users.api';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { formatDisplayDateTime } from '@/utils/date';
import { formatNumber, pluralize } from '@/utils/number';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  assignmentSubjectsQueryOptions,
  assignmentsQueryOptions,
  memberInstitutesQueryOptions,
} from '../api/question.api';
import {
  useCreateAssignment,
  useRemoveAssignment,
  useUpdateAssignedSubjects,
} from '../hooks/useQuestionMutations';
import { CollectorSelect } from './CollectorSelect';
import { DetailCard } from './DetailCard';
import { MemberInstituteSelect } from './MemberInstituteSelect';
import { getInstitutePapersLink } from './monitorLevelLinks';
import { SubjectTargetPicker } from './SubjectTargetPicker';
import { PROJECT_MANAGEMENT_PATH } from '../constants/question.constants';
import { isProjectOpen } from '../utils/projectWindow';
import type { AssignedSubjectPayload, Assignment, QuestionProject } from '../types/question.types';
import type { ColumnDef } from '@tanstack/react-table';

const AREA_SEPARATOR = ', ';

interface AssignmentPanelProps {
  project: QuestionProject;
  userId: number | undefined;
  onUserIdChange: (userId: number | undefined) => void;
}

export function AssignmentPanel({ project, userId, onUserIdChange }: AssignmentPanelProps) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Assignment | null>(null);
  const [removing, setRemoving] = useState<Assignment | null>(null);
  const lastViewedPapersId = useRevealLastViewedRow('questionInstitutePapers');

  const assignments = useQuery(assignmentsQueryOptions(project.projectId, userId));
  const isOpen = isProjectOpen(project.deadlineDate);

  const member = useQuery({
    ...userDetailQueryOptions(String(userId ?? 0)),
    enabled: userId !== undefined,
  });
  const memberName = member.data?.fullName ?? null;
  const takenInstituteIds = new Set(
    (assignments.data ?? []).map((row) => row.instituteId).filter((id) => id !== null),
  );
  const remove = useRemoveAssignment(() => setRemoving(null));

  const columns: ColumnDef<Assignment, unknown>[] = [
    createSerialColumn<Assignment>(),
    {
      accessorKey: 'instituteName',
      header: 'Institution Name (Code)',
      cell: ({ row }) => (
        <WrappingCellText>
          {row.original.instituteName ?? EMPTY_VALUE}
          {row.original.instituteCode === null ? null : (
            <span className="block text-xs tabular-nums text-muted-foreground">
              {row.original.instituteCode}
            </span>
          )}
        </WrappingCellText>
      ),
    },
    { id: 'zonalName', header: 'ZM Name', cell: () => memberName ?? EMPTY_VALUE },
    {
      id: 'where',
      header: 'Division, District, Thana',
      cell: ({ row }) =>
        [row.original.divisionName, row.original.districtName, row.original.thanaName]
          .filter(Boolean)
          .join(AREA_SEPARATOR) || EMPTY_VALUE,
    },
    { accessorKey: 'assignedCount', header: 'Total Subject Assign' },
    {
      accessorKey: 'collectedCount',
      header: 'Total Subject Collect',
      cell: ({ row }) => {
        const { collectedCount, instituteName } = row.original;
        const hint = `View ${formatNumber(collectedCount)} ${pluralize(collectedCount, 'collected subject')}`;
        return (
          <DrillDownCount
            count={collectedCount}
            hint={hint}
            ariaLabel={instituteName ? `${hint} of ${instituteName}` : hint}
            render={getInstitutePapersLink(row.original.projectInstituteId, {
              from: PROJECT_MANAGEMENT_PATH,
              projectId: project.projectId,
              userId,
              instituteId: row.original.instituteId ?? undefined,
              isCollectedOnly: true,
            })}
          />
        );
      },
    },
    {
      accessorKey: 'assignedAt',
      header: 'Assign Datetime',
      cell: ({ row }) => formatDisplayDateTime(row.original.assignedAt),
    },
    createActionsColumn<Assignment>((assignment) => {
      const instituteName = assignment.instituteName ?? 'this institute';
      return (
        <RowActionGroup>
          <RowActionButton
            label="Subjects"
            ariaLabel={`Subjects for ${instituteName}`}
            onClick={() => setEditing(assignment)}
          />
          <RowActionButton
            label="Take off"
            ariaLabel={`Take ${instituteName} off`}
            tone="destructive"
            onClick={() => setRemoving(assignment)}
          />
        </RowActionGroup>
      );
    }),
  ];

  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm lg:min-h-0 lg:flex-1">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-full max-w-sm">
          <FilterField id="ap-member" label="ZM Name">
            <CollectorSelect id="ap-member" value={userId} onChange={onUserIdChange} />
          </FilterField>
        </div>

        {isOpen ? (
          <Button
            type="button"
            size="sm"
            disabled={userId === undefined}
            onClick={() => setAdding(true)}
          >
            <Plus className="size-4" />
            Assign an institute
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Past its deadline - institutes can no longer be assigned.
          </p>
        )}
      </div>

      {userId === undefined ? (
        <EmptyState
          kind="pickFirst"
          size="compact"
          title="Pick a ZM"
          description="Pick a ZM to see what they collect on this project."
          className="lg:flex-1"
        />
      ) : (
        <DataTable
          data={assignments.data ?? []}
          columns={columns}
          isLoading={assignments.isLoading}
          className="lg:flex-1"
          stickyColumnId="instituteName"
          emptyMessage="This ZM has no institute on this project yet"
          isRowHighlighted={(row) => row.projectInstituteId === lastViewedPapersId}
        />
      )}

      {userId === undefined || !isOpen ? null : (
        <AssignInstituteDialog
          project={project}
          userId={userId}
          takenInstituteIds={takenInstituteIds}
          open={adding}
          onOpenChange={setAdding}
        />
      )}

      <EditSubjectsDialog
        project={project}
        assignment={editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Take this institute off?"
        description={`${removing?.instituteName ?? 'The institute'} stops being this ZM's on this project. Papers already handed in are kept.`}
        confirmLabel="Take it off"
        confirmVariant="destructive"
        isPending={remove.isPending}
        onConfirm={() => {
          if (removing !== null) remove.mutate(removing.projectInstituteId);
        }}
      />
    </div>
  );
}

interface AssignInstituteDialogProps {
  project: QuestionProject;
  userId: number;
  takenInstituteIds: Set<number>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AssignInstituteDialog({
  project,
  userId,
  takenInstituteIds,
  open,
  onOpenChange,
}: AssignInstituteDialogProps) {
  const [instituteId, setInstituteId] = useState<number | undefined>(undefined);
  const [subjects, setSubjects] = useState<AssignedSubjectPayload[]>([]);

  const { data: institutes } = useQuery(memberInstitutesQueryOptions(open ? userId : undefined));
  const picked = institutes?.find((row) => row.instituteId === instituteId);
  const isTaken = instituteId !== undefined && takenInstituteIds.has(instituteId);

  const create = useCreateAssignment(() => {
    setInstituteId(undefined);
    setSubjects([]);
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign an institute</DialogTitle>
          <DialogDescription>
            Only the institutes this member already covers can be picked.
          </DialogDescription>
        </DialogHeader>

        <FilterField id="ai-institute" label="Institute">
          <MemberInstituteSelect
            id="ai-institute"
            userId={open ? userId : undefined}
            value={instituteId}
            onChange={setInstituteId}
          />
        </FilterField>

        {isTaken ? (
          <p className="text-sm font-medium text-destructive">
            This institute is already assigned. Please select another institute.
          </p>
        ) : null}

        {picked === undefined ? null : (
          <DetailCard
            title="Institution"
            rows={[
              ['Institute', picked.instituteName],
              ['Code', picked.instituteCode === null ? null : String(picked.instituteCode)],
              ['EIIN', picked.instituteEiin],
              ['Division', picked.divisionName],
              ['District', picked.districtName],
              ['Thana', picked.thanaName],
            ]}
          />
        )}

        <div className="min-w-0">
          <p className="mb-2 text-sm font-medium">Subjects</p>
          <SubjectTargetPicker
            classId={project.classId ?? undefined}
            deadlineHint={project.deadlineDate}
            value={subjects}
            onChange={setSubjects}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              instituteId === undefined || isTaken || subjects.length === 0 || create.isPending
            }
            onClick={() => {
              if (instituteId === undefined) return;
              create.mutate({
                projectId: project.projectId,
                payload: { userId, instituteId, subjects },
              });
            }}
          >
            {create.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditSubjectsDialogProps {
  project: QuestionProject;
  assignment: Assignment | null;
  onOpenChange: (open: boolean) => void;
}

function EditSubjectsDialog({ project, assignment, onOpenChange }: EditSubjectsDialogProps) {
  const current = useQuery(assignmentSubjectsQueryOptions(assignment?.projectInstituteId));

  const [draft, setDraft] = useState<AssignedSubjectPayload[] | null>(null);
  const update = useUpdateAssignedSubjects(() => {
    setDraft(null);
    onOpenChange(false);
  });

  const value =
    draft ??
    (current.data ?? []).map((row) => ({
      subjectId: row.subjectId ?? 0,
      ...(row.targetDate === null ? {} : { targetDate: row.targetDate.slice(0, 10) }),
    }));

  return (
    <Dialog
      open={assignment !== null}
      onOpenChange={(open) => {
        if (!open) setDraft(null);
        onOpenChange(open);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{assignment?.instituteName ?? 'Subjects'}</DialogTitle>
          <DialogDescription>
            Untick a subject to drop it. It is deactivated rather than deleted, because a paper may
            already be attached to it.
          </DialogDescription>
        </DialogHeader>

        {current.isLoading ? (
          <ListSkeleton />
        ) : (
          <SubjectTargetPicker
            classId={project.classId ?? undefined}
            deadlineHint={project.deadlineDate}
            value={value}
            onChange={setDraft}
          />
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={update.isPending}
            onClick={() => {
              if (assignment === null) return;
              update.mutate({
                projectInstituteId: assignment.projectInstituteId,
                subjects: value,
              });
            }}
          >
            {update.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
